import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { SUPPORT_EMAIL } from '../../legal';

/**
 * Minimal markdown renderer tuned for DealShaq legal docs.
 *
 * Why hand-rolled instead of react-markdown?
 *  - All seven legal docs are author-controlled, trusted source; no XSS risk.
 *  - Adding react-markdown + remark-gfm would inflate the bundle by ~80 KB
 *    for content that uses only #/##/###, **bold**, and blank lines.
 *  - This keeps the legal route shells under 200 LOC total.
 */
function renderMarkdown(md) {
  const lines = md.split('\n');
  const blocks = [];
  let key = 0;

  const inline = (text) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
      chunk.startsWith('**') && chunk.endsWith('**')
        ? <strong key={i}>{chunk.slice(2, -2)}</strong>
        : chunk
    );

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) continue;
    if (line.startsWith('### ')) {
      blocks.push(<h3 key={key++} className="text-lg font-semibold mt-6 mb-2 text-gray-800">{inline(line.slice(4))}</h3>);
    } else if (line.startsWith('## ')) {
      blocks.push(<h2 key={key++} className="text-xl font-bold mt-8 mb-3 text-gray-900">{inline(line.slice(3))}</h2>);
    } else if (line.startsWith('# ')) {
      blocks.push(<h2 key={key++} className="text-2xl font-bold mt-8 mb-4 text-gray-900 border-b border-gray-200 pb-2">{inline(line.slice(2))}</h2>);
    } else {
      blocks.push(<p key={key++} className="text-gray-700 leading-relaxed mb-3">{inline(line)}</p>);
    }
  }
  return blocks;
}

/**
 * Reusable rendering surface for any legal document.
 * Props:
 *   doc          – the LEGAL_DOCS entry to render
 *   showBackLink – whether to render the "← Back" header link (default true)
 */
export default function LegalPage({ doc, showBackLink = true }) {
  const navigate = useNavigate();
  const rendered = useMemo(() => renderMarkdown(doc.body), [doc.body]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid={`legal-page-${doc.id}`}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {showBackLink && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-sm text-emerald-700 hover:text-emerald-800 mb-6"
            data-testid="legal-back-link"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </button>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-10">
          <header className="mb-6 pb-6 border-b border-gray-100">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">{doc.title}</h1>
            <p className="text-sm text-gray-500 mt-2">
              Effective Date: <span className="font-medium">{doc.effectiveDate}</span>
            </p>
          </header>

          <article className="text-base">{rendered}</article>

          <footer className="mt-10 pt-6 border-t border-gray-100 flex items-center text-sm text-gray-500">
            <Mail className="w-4 h-4 mr-2" />
            Questions? Email{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="ml-1 text-emerald-700 hover:text-emerald-800 underline"
              data-testid="legal-support-link"
            >
              {SUPPORT_EMAIL}
            </a>
          </footer>
        </div>
      </div>
    </div>
  );
}
